import { createContext } from '@direct-frontend/modules-context';

import type { QueryActionCreatorResult } from '../rtk-query-types';
import type { AnyQueryDefinition } from './types';

/**
 * Запись о предзагруженном запросе в кеше prefetchManager
 */
type PrefetchEntry = {
    /**
     * Ключ кеша запроса (queryCacheKey)
     */
    key: string;
    /**
     * Промис запроса с возможностью отписки
     */
    promise: QueryActionCreatorResult<AnyQueryDefinition>;
    /**
     * Время хранения предзагруженных данных в секундах
     */
    keepPrefetchedDataFor: number;
    /**
     * Таймер для автоматического удаления PrefetchEntry из кеша
     */
    timeout?: ReturnType<typeof setTimeout>;
    /**
     * Флаг удаления записи из кеша
     */
    removed?: boolean;
};

type PrefetchManagerContext = Map<string, PrefetchEntry>;

const { getContext } = createContext<PrefetchManagerContext>({
    name: '@direct-frontend/modules-store/prefetchManager',
    getInitialValue: () => new Map(),
});

const defaultKeepPrefetchedDataFor = 60;

/**
 * Набор методов для работы с промисами для prefetch запросов.
 */
export const prefetchManager = {
    get(queryCacheKey: string) {
        const entry = getContext().get(queryCacheKey);
        if (!entry) {
            return;
        }

        if (entry.timeout) {
            resetTimeout(entry);
        }

        return entry.promise;
    },

    set(
        queryCacheKey: string,
        options: {
            promise: QueryActionCreatorResult<AnyQueryDefinition>;
            keepPrefetchedDataFor?: number;
        }
    ) {
        const context = getContext();
        const { promise, keepPrefetchedDataFor = defaultKeepPrefetchedDataFor } = options;

        if (process.env.NODE_ENV !== 'production') {
            validateKeepPrefetchedDataFor(keepPrefetchedDataFor);
        }

        let entry = context.get(queryCacheKey);
        if (entry) {
            // Удаляем старую подписку, так как была создана новая на тот же ключ кеша.
            entry.promise.unsubscribe();

            entry.keepPrefetchedDataFor = Math.max(entry.keepPrefetchedDataFor, keepPrefetchedDataFor);
            entry.promise = promise;
        } else {
            entry = {
                key: queryCacheKey,
                promise,
                keepPrefetchedDataFor,
            };
            context.set(queryCacheKey, entry);
        }

        promise.then(() => {
            // Для устаревших промисов ничего не делаем, чтобы не запускать несколько одинаковых таймеров.
            if (entry.promise !== promise || entry.removed) {
                return;
            }
            resetTimeout(entry);
        });
    },

    remove(queryCacheKey: string) {
        const entry = getContext().get(queryCacheKey);
        if (!entry) {
            return;
        }

        if (entry.timeout) {
            clearTimeout(entry.timeout);
        }

        // Нужно удалить подписку, чтобы данные не остались в кеше навечно из-за лишней подписки.
        // Предзагруженные данные останутся в сторе на время keepUnusedDataFor, который
        // указан в настройках endpoint (по умолчанию 60 сек).
        entry.promise.then(() => {
            // Подписку необходимо удалять строго после выполнения запроса, иначе
            // при отсутствии других подписок данные не будут сохранены в стор.
            entry.promise.unsubscribe();
        });

        entry.removed = true;

        getContext().delete(entry.key);
    },

    clear() {
        for (const entry of getContext().values()) {
            if (entry.timeout) {
                clearTimeout(entry.timeout);
            }

            entry.removed = true;
        }

        getContext().clear();
    },
};

/**
 * При каждом обращении к кешу продлеваем таймаут на хранение данных.
 * Это нужно для того, чтобы время до удаления данных рассчитывалось с
 * момента последнего рендера компонента (даже если он упал в Suspense).
 *
 * @param entry Запись в контексте
 */
function resetTimeout(entry: PrefetchEntry) {
    // На сервере нет смысла пытаться очистить кеш, так как на сервере
    // не запускаются механизмы наполнения кеша и подписки ничего не делают.
    if (process.env.BUILD_TARGET !== 'client') {
        return;
    }

    if (entry.timeout) {
        clearTimeout(entry.timeout);
    }

    entry.timeout = setTimeout(() => {
        prefetchManager.remove(entry.key);
    }, entry.keepPrefetchedDataFor * 1000);
}

/**
 * Проверяет значение keepPrefetchedDataFor на соответствие правилам:
 * 1. Значение должно быть безопасным для использования внутри setTimeout.
 * 2. Значение не должно быть слишком маленьким, иначе может некорректно работать Suspense.
 *
 * @param keepPrefetchedDataFor Значение параметра
 */
function validateKeepPrefetchedDataFor(keepPrefetchedDataFor: number) {
    // Значение таймаута должно укладываться в 32bit signed integer.
    // Перед передачей в setTimeout мы умножаем keepPrefetchedDataFor на 1000.
    const maxTimeoutSafeValue = Math.floor(2147483647 / 1000);

    // Не разрешаем выставлять значение меньше минуты, так как при слишком
    // маленьких значениях может некорректно работать Suspense.
    const minValueForSuspense = 60;

    if (typeof keepPrefetchedDataFor !== 'number') {
        return;
    }

    if (keepPrefetchedDataFor < minValueForSuspense) {
        throw new Error(
            `Задано слишком маленькое значение keepPrefetchedDataFor. Значение должно быть не менее ${minValueForSuspense}.`
        );
    }

    if (keepPrefetchedDataFor > maxTimeoutSafeValue) {
        throw new Error(
            `Задано слишком большое значение keepPrefetchedDataFor. Значение должно быть не более ${maxTimeoutSafeValue}.`
        );
    }
}
